import { useState, useCallback } from 'react';
import { 
  uploadDocument, 
  deleteDocument, 
  validateFile,
  formatFileSize 
} from '../services/uploadService';

/**
 * Custom Hook: useFileUpload
 * Manages file upload state, progress, and operations
 */
const useFileUpload = (reportId = null, options = {}) => {
  const {
    maxFiles = 5,
    maxSize = 10 * 1024 * 1024, // 10MB
    allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'text/plain'
    ],
    onUploadSuccess = null,
    onUploadError = null,
    onDeleteSuccess = null
  } = options;

  // State management
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({});
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);

  /**
   * Add file to upload queue with validation
   */
  const addFile = useCallback((file) => {
    // Check max files limit
    if (files.length >= maxFiles) {
      setErrors(prev => ({
        ...prev,
        general: `Maximum ${maxFiles} files allowed`
      }));
      return false;
    }

    // Validate file
    const validation = validateFile(file, { maxSize, allowedTypes });
    
    if (!validation.valid) {
      setErrors(prev => ({
        ...prev,
        [file.name]: validation.error
      }));
      return false;
    }

    // Clear any previous errors for this file
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[file.name];
      delete newErrors.general;
      return newErrors;
    });

    // Add file to queue
    const fileObject = {
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      sizeFormatted: formatFileSize(file.size),
      status: 'pending', // pending, uploading, success, error
      uploadedPath: null,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    };

    setFiles(prev => [...prev, fileObject]);
    return true;
  }, [files.length, maxFiles, maxSize, allowedTypes]);

  /**
   * Add multiple files
   */
  const addFiles = useCallback((fileList) => {
    const filesArray = Array.from(fileList);
    let addedCount = 0;

    filesArray.forEach(file => {
      if (addFile(file)) {
        addedCount++;
      }
    });

    return addedCount;
  }, [addFile]);

  /**
   * Remove file from queue (before upload)
   */
  const removeFile = useCallback((fileId) => {
    setFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      
      // Revoke object URL if it exists
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      
      return prev.filter(f => f.id !== fileId);
    });

    // Clear any errors for this file
    setErrors(prev => {
      const newErrors = { ...prev };
      const file = files.find(f => f.id === fileId);
      if (file) {
        delete newErrors[file.name];
      }
      return newErrors;
    });

    // Clear progress for this file
    setProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
  }, [files]);

  /**
   * Upload a single file
   */
  const uploadFile = useCallback(async (fileObject) => {
    try {
      // Update file status to uploading
      setFiles(prev => prev.map(f => 
        f.id === fileObject.id ? { ...f, status: 'uploading' } : f
      ));

      // Upload with progress tracking
      const response = await uploadDocument(
        fileObject.file,
        reportId,
        (percent) => {
          setProgress(prev => ({
            ...prev,
            [fileObject.id]: percent
          }));
        }
      );

      // Update file status to success and remove from files list after a delay
      // This prevents showing duplicates when parent component updates initialFiles
      setFiles(prev => prev.map(f => 
        f.id === fileObject.id 
          ? { ...f, status: 'success', uploadedPath: response.path || response.filePath } 
          : f
      ));

      // Remove successfully uploaded file from the pending list after callback
      // Parent component will show it via initialFiles
      setTimeout(() => {
        setFiles(prev => prev.filter(f => f.id !== fileObject.id));
      }, 1000);

      // Call success callback first so parent can update its state
      if (onUploadSuccess) {
        onUploadSuccess(response);
      }

      return response;
    } catch (error) {
      // Update file status to error
      setFiles(prev => prev.map(f => 
        f.id === fileObject.id ? { ...f, status: 'error' } : f
      ));

      const errorMessage = error.response?.data?.message || error.message || 'Upload failed';
      setErrors(prev => ({
        ...prev,
        [fileObject.name]: errorMessage
      }));

      // Call error callback
      if (onUploadError) {
        onUploadError(error);
      }

      throw error;
    }
  }, [reportId, onUploadSuccess, onUploadError]);

  /**
   * Upload all pending files
   */
  const uploadAll = useCallback(async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');

    if (pendingFiles.length === 0) {
      return { success: true, uploaded: 0 };
    }

    setUploading(true);
    setErrors({});

    const results = [];

    try {
      // Upload files sequentially to avoid overwhelming the server
      for (const fileObject of pendingFiles) {
        try {
          const result = await uploadFile(fileObject);
          results.push({ success: true, file: fileObject.name, result });
        } catch (error) {
          results.push({ success: false, file: fileObject.name, error });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      return {
        success: failCount === 0,
        uploaded: successCount,
        failed: failCount,
        results
      };
    } finally {
      setUploading(false);
    }
  }, [files, uploadFile]);

  /**
   * Delete an uploaded file
   */
  const deleteFile = useCallback(async (filePath, fileId = null) => {
    try {
      await deleteDocument(filePath, reportId);

      // Remove from uploaded files
      setUploadedFiles(prev => prev.filter(f => f.path !== filePath));

      // Remove from files if fileId provided
      if (fileId) {
        removeFile(fileId);
      }

      // Call success callback
      if (onDeleteSuccess) {
        onDeleteSuccess(filePath);
      }

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Delete failed';
      setErrors(prev => ({
        ...prev,
        delete: errorMessage
      }));
      throw error;
    }
  }, [reportId, removeFile, onDeleteSuccess]);

  /**
   * Clear all files
   */
  const clearAll = useCallback(() => {
    // Revoke all object URLs
    files.forEach(f => {
      if (f.preview) {
        URL.revokeObjectURL(f.preview);
      }
    });

    setFiles([]);
    setProgress({});
    setErrors({});
  }, [files]);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    clearAll();
    setUploadedFiles([]);
    setUploading(false);
  }, [clearAll]);

  /**
   * Check if any files are being uploaded
   */
  const isUploading = uploading || files.some(f => f.status === 'uploading');

  /**
   * Check if all files uploaded successfully
   */
  const allUploaded = files.length > 0 && files.every(f => f.status === 'success');

  /**
   * Get files by status
   */
  const getFilesByStatus = useCallback((status) => {
    return files.filter(f => f.status === status);
  }, [files]);

  return {
    // State
    files,
    uploading: isUploading,
    progress,
    errors,
    uploadedFiles,
    allUploaded,
    
    // Actions
    addFile,
    addFiles,
    removeFile,
    uploadFile,
    uploadAll,
    deleteFile,
    clearAll,
    reset,
    getFilesByStatus
  };
};

export default useFileUpload;
